import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { surveillanceReportApi } from '../api/api';
import type { 
  SurveillanceAlertFormData, 
  ProjectClient, 
  ClientUser 
} from '../types/types';

export const useSurveillanceReportForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<SurveillanceAlertFormData>({
    project: '',
    subject: '',
    result: '',
    description: '',
    conclusion: '',
    media_files: null,
    client_notified: false,
    notified_user: '',
    clientSelect: '',
  });

  // Project clients state
  const [projectClients, setProjectClients] = useState<ProjectClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Client users state
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load clients when project changes and notification is checked
  const loadProjectClients = useCallback(async (projectId: string) => {
    if (!projectId) {
      setProjectClients([]);
      return;
    }

    console.log('Loading clients for project:', projectId);
    setLoadingClients(true);
    try {
      const response = await surveillanceReportApi.getProjectClients(Number(projectId));
      console.log('Loaded clients:', response.clients);
      setProjectClients(response.clients || []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setProjectClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  // Load users when client changes
  const loadClientUsers = useCallback(async (clientId: string) => {
    if (!clientId) {
      setClientUsers([]);
      return;
    }

    console.log('Loading users for client:', clientId);
    setLoadingUsers(true);
    try {
      const response = await surveillanceReportApi.getClientUsers(Number(clientId));
      console.log('Loaded users:', response.users);
      setClientUsers(response.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setClientUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Handle project change
  useEffect(() => {
    if (formData.client_notified && formData.project) {
      loadProjectClients(formData.project);
      // Reset client and user selections when project changes
      setFormData(prev => ({
        ...prev,
        clientSelect: '',
        notified_user: '',
      }));
      setClientUsers([]);
    }
  }, [formData.project, formData.client_notified, loadProjectClients]);

  // Handle client change
  useEffect(() => {
    if (formData.clientSelect) {
      loadClientUsers(formData.clientSelect);
      // Reset user selection when client changes
      setFormData(prev => ({
        ...prev,
        notified_user: '',
      }));
    }
  }, [formData.clientSelect, loadClientUsers]);

  // Handle form field changes
  const handleChange = useCallback((
    field: keyof SurveillanceAlertFormData,
    value: string | boolean | FileList | null
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  }, [error, success]);

  // Handle checkbox change
  const handleNotificationCheckboxChange = useCallback((checked: boolean) => {
    console.log('Notification checkbox changed:', checked, 'Current project:', formData.project);
    
    setFormData(prev => ({
      ...prev,
      client_notified: checked,
      clientSelect: checked ? prev.clientSelect : '',
      notified_user: checked ? prev.notified_user : '',
    }));

    if (!checked) {
      setProjectClients([]);
      setClientUsers([]);
    } else if (formData.project) {
      console.log('Loading clients for project:', formData.project);
      loadProjectClients(formData.project);
    }
  }, [formData.project, loadProjectClients]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    if (!formData.project) {
      setError('Please select a project');
      return false;
    }

    if (!formData.subject.trim()) {
      setError('Please provide a subject');
      return false;
    }

    if (!formData.result) {
      setError('Please select a result');
      return false;
    }

    if (!formData.description.trim()) {
      setError('Please provide a description');
      return false;
    }

    if (!formData.conclusion.trim()) {
      setError('Please provide a conclusion');
      return false;
    }

    if (formData.client_notified) {
      if (!formData.clientSelect) {
        setError('Please select a client');
        return false;
      }
      if (!formData.notified_user) {
        setError('Please select a user who was notified');
        return false;
      }
    }

    return true;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const submitData = new FormData();
      submitData.append('project', formData.project);
      submitData.append('subject', formData.subject);
      submitData.append('result', formData.result);
      submitData.append('description', formData.description);
      submitData.append('conclusion', formData.conclusion);
      submitData.append('client_notified', String(formData.client_notified));

      // Handle multiple file uploads
      if (formData.media_files && formData.media_files.length > 0) {
        Array.from(formData.media_files).forEach((file) => {
          submitData.append('media_files', file);
        });
      }

      if (formData.client_notified && formData.notified_user) {
        submitData.append('notified_user', formData.notified_user);
      }

      await surveillanceReportApi.createAlert(submitData);
      
      setSuccess('Surveillance alert created successfully!');
      
      // Reset form
      setFormData({
        project: '',
        subject: '',
        result: '',
        description: '',
        conclusion: '',
        media_files: null,
        client_notified: false,
        notified_user: '',
        clientSelect: '',
      });
      setProjectClients([]);
      setClientUsers([]);

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/app/dashboard');
      }, 2000);
    } catch (err: any) {
      console.error('Error creating alert:', err);
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to create surveillance alert. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, navigate]);

  return {
    formData,
    handleChange,
    handleNotificationCheckboxChange,
    handleSubmit,
    isSubmitting,
    error,
    success,
    projectClients,
    loadingClients,
    clientUsers,
    loadingUsers,
  };
};

